﻿// 🔗 PO editing (v1) — self-contained frontend helpers + API.
// Kept isolated so the whole feature can be reverted without git.

import { gloggedinuser } from "../../../interfaces";
import { getHeaderG } from "../../../api/qpi";

// positional map into the configured field list (mirrors backend kgnf_po_edit_v1.py)
export const PO_ED_V1_POS_ORDER_ID = 0;   // field whose value is the PO id (dropdown value)
export const PO_ED_V1_POS_ORDER_DATE = 1; // field whose value is the order date
export const PO_ED_V1_POS_ITEM_QTY = 2;   // PO-side ordered quantity
export const PO_ED_V1_POS_ITEM_PRICE = 3; // PO-side unit price
export const PO_ED_V1_POS_ITEM_TOTAL = 4; // PO-side line total

// 💡 example props_to_show setting value:
// purchase_order,release_date,item_quantity,item_unit_price,item_total_price,item_description,contractor_name,contractor_vat_identification



// is PO editing enabled for this space
export const isPoEditingEnabled_po_ed_v1 = (selectedGroup: any): boolean => {
    return (selectedGroup?.extractionfieldlist?.po_editing_enabled ?? 0) == 1;
};

// configured fields: [0] = dropdown value field, [1..] = fields shown per row
export const getPoEditingFields_po_ed_v1 = (selectedGroup: any): string[] => {
    return String(selectedGroup?.extractionfieldlist?.props_to_show || "")
        .split(",")
        .map((f: string) => f.trim())
        .filter((f: string) => f.length > 0);
};

// source file id holding the PO rows: instr entry with fieldid == 'invoice_item' ⇒ validation_source
export const getPoSourceFid_po_ed_v1 = (selectedGroup: any): string => {
    const instr = selectedGroup?.extractionfieldlist?.instr || [];
    const entry = (Array.isArray(instr) ? instr : []).find((i: any) => i?.fieldid === "invoice_items");
    return String(entry?.validation_source || "");
};

export interface poEditV1Row {
    __po_row_idx: number;
    __po_row_id?: string | number | null;   // unique DB key of the PO line
    [field: string]: any;
}

export interface poEditV1ChoicesResult {
    status: string;
    rows: poEditV1Row[];
    fields: string[];
}

// fetch selectable PO rows from the source file
// LINK app/backend/kgnf_po_edit_v1.py#po_ed_v1_choices
// ANCHOR[id=fetchPoChoices_po_ed_v1]
export const fetchPoChoices_po_ed_v1 = async (
    loggedInUser: gloggedinuser,
    sourceFid: string,
    fields: string[],
    contractorVat: string,
    purchaseOrder: string,      // root invoice purchase_order string; may be ';'-delimited multi-PO — sent as-is
): Promise<poEditV1ChoicesResult> => {
    const response = await fetch("/po_ed_v1_choices", {
        method: "POST",
        headers: getHeaderG(loggedInUser),
        body: JSON.stringify({ source_fid: sourceFid, fields, contractor_vat_identification: contractorVat, purchase_order: purchaseOrder })
    });
    if (!response.ok) return { status: "error", rows: [], fields: [] };
    return await response.json();
};

// search ALL PO rows in the source file, scoped only by the space id (no VAT / PO-id filter)
// LINK app/backend/kgnf_po_edit_v1.py#po_ed_v1_search
// ANCHOR[id=searchPoChoices_po_ed_v1]
export const searchPoChoices_po_ed_v1 = async (
    loggedInUser: gloggedinuser,
    sourceFid: string,
    fields: string[],
    term: string,
    spaceId: string,
): Promise<poEditV1ChoicesResult> => {
    const response = await fetch("/po_ed_v1_search", {
        method: "POST",
        headers: getHeaderG(loggedInUser),
        body: JSON.stringify({ source_fid: sourceFid, fields, term, space_id: spaceId })
    });
    if (!response.ok) return { status: "error", rows: [], fields: [] };
    return await response.json();
};

// apply a new PO match to one invoice line item; returns updated invoicedata dict
// LINK app/backend/kgnf_po_edit_v1.py#po_ed_v1_update
export const updatePoMatch_po_ed_v1 = async (
        loggedInUser: gloggedinuser,
        fid: string,
        sourceFid: string,
        itemNumber: any,        // unique identifier for the invoice line
        selectedIdx: number,    // unique DB key for chosen PO
        fields: string[]
    ): Promise<{ status: string; invoicedata?: any; message?: string }> => {
    const response = await fetch("/po_ed_v1_update", {
        method: "POST",
        headers: getHeaderG(loggedInUser),
        body: JSON.stringify({
            fid,
            source_fid: sourceFid,
            item_number: itemNumber,
            selected_idx: selectedIdx,
            fields
        })
    });
    if (!response.ok) return response?.json() || { status: "error" };
    return await response.json();
};

// assign SEVERAL PO lines to one invoice line item ([0] = primary → reconciliation, rest → reconciliation.multi_assign_pos)
// LINK app/backend/kgnf_po_edit_v1.py#po_ed_v1_update_multipo
// ANCHOR[id=updatePoMatchMulti_po_ed_v1]
export const updatePoMatchMulti_po_ed_v1 = async (
        loggedInUser: gloggedinuser,
        fid: string,
        sourceFid: string,
        itemNumber: any,
        selectedIdxs: number[],  // unique DB keys of chosen PO lines, ordered (primary first)
        fields: string[]
    ): Promise<{ status: string; invoicedata?: any; message?: string }> => {
    const response = await fetch("/po_ed_v1_update_multipo", {
        method: "POST",
        headers: getHeaderG(loggedInUser),
        body: JSON.stringify({
            fid,
            source_fid: sourceFid,
            item_number: itemNumber,
            selected_idxs: selectedIdxs,
            fields
        })
    });
    if (!response.ok) return { status: "error" };
    return await response.json();
};

// extra PO lines assigned to one invoice line alongside the primary `reconciliation` (mirrors backend MULTI_ASSIGN_KEY)
export const MULTI_ASSIGN_KEY_po_ed_v1 = "multi_assign_pos";

// unique id of an invoice line (mirrors backend: item_mapping_number, else item_number — discount rows have no mapping number)
export const invItemId_po_ed_v1 = (it: any): string => String(it?.item_mapping_number ?? it?.item_number ?? "").trim();

// pick the matched_order_items row for (PO line uid, invoice line). A catch-all PO line is duplicated once per
// invoice line, so twins share the PO id and are told apart by ai_recon.invoice_item_number.
// Never guess between several twins — an unmatched invoice line must not overwrite another line's row.
const findMatchedTwin_po_ed_v1 = (matched: any[], rowUid: string, itemId: string): any => {
    const sameId = matched.filter((m: any) => String(m?.id ?? "").trim() === rowUid);
    const exact  = sameId.find((m: any) => String(m?.ai_recon?.invoice_item_number ?? "").trim() === itemId);
    if (exact) return exact;
    return sameId.length === 1 && (sameId[0]?.ai_recon?.invoice_item_number ?? "") === "" ? sameId[0] : null;
};

export const getItemMultiAssignPos_po_ed_v1 = (item: any): any[] => {
    const extra = item?.reconciliation?.[MULTI_ASSIGN_KEY_po_ed_v1];
    return Array.isArray(extra) ? extra.filter((r: any) => r && typeof r === "object") : [];
};

// unique DB keys of ALL PO lines assigned to this invoice item (primary first)
export const getItemPoRowIds_po_ed_v1 = (item: any): string[] => {
    const primary = getItemPoRowId_po_ed_v1(item);
    if (!primary) return [];
    const extra = getItemMultiAssignPos_po_ed_v1(item).map((r: any) => String(r?.id ?? "").trim()).filter((s: string) => s !== "");
    return Array.from(new Set([primary, ...extra]));
};

// current PO id shown on a line item (per-item purchase_order.order_id_value)
export const getItemPoId_po_ed_v1 = (item: any): string => {
    return String(item?.purchase_order?.order_id_value ?? "").trim();
};

// unique DB key of the PO line currently matched to this invoice item (reconciliation.id)
export const getItemPoRowId_po_ed_v1 = (item: any): string => {
    return String(item?.reconciliation?.id ?? "").trim();
};

// numeric parse tolerant of strings, commas and blanks (mirrors backend _to_num)
const toNum_po_ed_v1 = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return val;
    const n = parseFloat(String(val).replace(/,/g, "").trim());
    return isNaN(n) ? 0 : n;
};

const round4_po_ed_v1 = (n: number): number => Math.round(n * 10000) / 10000;

// compute the ai_recon comparison numbers from the LIVE invoice item + selected PO row.
// invoice side: item.quantity / unit_price / total_price; PO side: positional fields [2..4].
export const buildAiReconNumbers_po_ed_v1 = (item: any, row: any, fields: string[]): Record<string, any> => {
    const idField    = fields[PO_ED_V1_POS_ORDER_ID];
    const qtyField   = fields[PO_ED_V1_POS_ITEM_QTY];
    const priceField = fields[PO_ED_V1_POS_ITEM_PRICE];
    const totalField = fields[PO_ED_V1_POS_ITEM_TOTAL];

    const invQty   = toNum_po_ed_v1(item?.quantity);
    const invPrice = toNum_po_ed_v1(item?.unit_price);
    const invTotal = toNum_po_ed_v1(item?.total_price);

    const ordQty   = qtyField   ? toNum_po_ed_v1(row?.[qtyField])   : 0;
    const ordPrice = priceField ? toNum_po_ed_v1(row?.[priceField]) : 0;
    const ordTotal = totalField ? toNum_po_ed_v1(row?.[totalField]) : 0;

    const nums: Record<string, any> = {
        quantity_received     : invQty,
        received_item_total   : invTotal,
    };
    if (idField)    nums.purchase_order_id        = String(row?.[idField] ?? "");
    if (qtyField)   nums.quantity_ordered         = ordQty;
    if (qtyField)   nums.difference_in_quantity   = round4_po_ed_v1(ordQty - invQty);
    if (priceField) nums.difference_in_unit_price = round4_po_ed_v1(ordPrice - invPrice);
    if (totalField) nums.ordered_item_total       = ordTotal;
    return nums;
};

// patch the edited invoice item's ai_recon (and its matched_order_items twin) with live numbers.
// keeps AI reasoning text intact; ensures the comparison numbers reflect the change immediately.
export const patchInvoiceDataAiRecon_po_ed_v1 = (
    invoiceData: any,
    liveItem: any,
    selectedRow: any,
    fields: string[]
): any => {
    if (!invoiceData || typeof invoiceData !== "object") return invoiceData;

    const nums    = buildAiReconNumbers_po_ed_v1(liveItem, selectedRow, fields);
    const itemNum = invItemId_po_ed_v1(liveItem);
    const rowUid  = String(selectedRow?.__po_row_id ?? "").trim();

    const items = Array.isArray(invoiceData.invoice_items) ? invoiceData.invoice_items : [];
    const target = itemNum ? items.find((it: any) => invItemId_po_ed_v1(it) === itemNum) : undefined;

    if (target && target.reconciliation && typeof target.reconciliation === "object") {
        target.reconciliation.ai_recon = { ...(target.reconciliation.ai_recon || {}), ...nums };
    }

    // keep this invoice line's matched_order_items twin consistent (see findMatchedTwin_po_ed_v1)
    const matched = invoiceData?.purchase_order_info?.matched_order_items;
    if (Array.isArray(matched) && rowUid && itemNum) {
        const twin = findMatchedTwin_po_ed_v1(matched, rowUid, itemNum);
        if (twin && typeof twin === "object") {
            twin.ai_recon = { ...(twin.ai_recon || {}), ...nums };
        }
    }

    refreshDiscrepancyCount_po_ed_v1(invoiceData, items);
    return invoiceData;
};

// multi-PO counterpart of buildAiReconNumbers_po_ed_v1 (mirrors backend _compute_multi_recon):
// - aggregate: invoice line vs SUM of all assigned PO lines (qty-weighted PO unit price)
// - perLine:   invoice qty allocated greedily in order (last line takes the remainder), total split by allocated qty
export const buildMultiAiReconNumbers_po_ed_v1 = (
    item: any,
    rows: any[],
    fields: string[]
): { aggregate: Record<string, any>; perLine: Record<string, any>[] } => {
    const idField    = fields[PO_ED_V1_POS_ORDER_ID];
    const qtyField   = fields[PO_ED_V1_POS_ITEM_QTY];
    const priceField = fields[PO_ED_V1_POS_ITEM_PRICE];
    const totalField = fields[PO_ED_V1_POS_ITEM_TOTAL];

    const invQty   = toNum_po_ed_v1(item?.quantity);
    const invPrice = toNum_po_ed_v1(item?.unit_price);
    const invTotal = toNum_po_ed_v1(item?.total_price);

    const nums = rows.map(r => ({
        qty:   qtyField   ? toNum_po_ed_v1(r?.[qtyField])   : 0,
        price: priceField ? toNum_po_ed_v1(r?.[priceField]) : 0,
        total: totalField ? toNum_po_ed_v1(r?.[totalField]) : 0,
    }));
    const sumQty = nums.reduce((a, n) => a + n.qty, 0);
    const sumTot = nums.reduce((a, n) => a + n.total, 0);
    const wPrice = sumQty
        ? nums.reduce((a, n) => a + n.qty * n.price, 0) / sumQty
        : (nums.length ? nums.reduce((a, n) => a + n.price, 0) / nums.length : 0);

    const poIds = idField
        ? Array.from(new Set(rows.map(r => String(r?.[idField] ?? "").trim()).filter(s => s !== "")))
        : [];

    const aggregate: Record<string, any> = { quantity_received: invQty, received_item_total: invTotal };
    if (idField)    aggregate.purchase_order_id        = poIds.join(";");
    if (qtyField)   aggregate.quantity_ordered         = round4_po_ed_v1(sumQty);
    if (qtyField)   aggregate.difference_in_quantity   = round4_po_ed_v1(sumQty - invQty);
    if (priceField) aggregate.difference_in_unit_price = round4_po_ed_v1(wPrice - invPrice);
    if (totalField) aggregate.ordered_item_total       = round4_po_ed_v1(sumTot);

    const perLine: Record<string, any>[] = [];
    let remaining = invQty;
    let allocTotSum = 0;
    rows.forEach((r, i) => {
        const n = nums[i];
        const isLast = i === rows.length - 1;
        const alloc    = isLast ? remaining : (n.qty > 0 ? Math.min(Math.max(remaining, 0), n.qty) : 0);
        const allocTot = isLast ? invTotal - allocTotSum : (invQty ? invTotal * alloc / invQty : 0);
        remaining   -= alloc;
        allocTotSum += allocTot;

        const line: Record<string, any> = { received_item_total: round4_po_ed_v1(allocTot) };
        if (idField)    line.purchase_order_id        = String(r?.[idField] ?? "");
        if (qtyField)   line.quantity_ordered         = n.qty;
        if (qtyField)   line.quantity_received        = round4_po_ed_v1(alloc);
        if (qtyField)   line.difference_in_quantity   = round4_po_ed_v1(n.qty - alloc);
        if (priceField) line.difference_in_unit_price = round4_po_ed_v1(n.price - invPrice);
        if (totalField) line.ordered_item_total       = n.total;
        perLine.push(line);
    });
    return { aggregate, perLine };
};

// multi-PO counterpart of patchInvoiceDataAiRecon_po_ed_v1: patches the aggregate on reconciliation.ai_recon,
// the per-line numbers on each multi_assign_pos entry, and every matched_order_items twin — from the LIVE grid item.
export const patchInvoiceDataAiReconMulti_po_ed_v1 = (
    invoiceData: any,
    liveItem: any,
    selectedRows: any[],     // ordered, primary first
    fields: string[]
): any => {
    if (!invoiceData || typeof invoiceData !== "object" || selectedRows.length === 0) return invoiceData;

    const { aggregate, perLine } = buildMultiAiReconNumbers_po_ed_v1(liveItem, selectedRows, fields);
    const itemNum = invItemId_po_ed_v1(liveItem);
    const items   = Array.isArray(invoiceData.invoice_items) ? invoiceData.invoice_items : [];
    const target  = itemNum ? items.find((it: any) => invItemId_po_ed_v1(it) === itemNum) : undefined;
    const recon   = target?.reconciliation;

    if (recon && typeof recon === "object") {
        recon.ai_recon = { ...(recon.ai_recon || {}), ...aggregate };
        const extras = Array.isArray(recon[MULTI_ASSIGN_KEY_po_ed_v1]) ? recon[MULTI_ASSIGN_KEY_po_ed_v1] : [];
        extras.forEach((ex: any) => {
            const idx = selectedRows.findIndex(r => String(r?.__po_row_id ?? "").trim() === String(ex?.id ?? "").trim());
            if (idx > 0) ex.ai_recon = { ...(ex.ai_recon || {}), ...perLine[idx] };
        });
    }

    const matched = invoiceData?.purchase_order_info?.matched_order_items;
    if (Array.isArray(matched)) {
        selectedRows.forEach((r, i) => {
            const uid  = String(r?.__po_row_id ?? "").trim();
            if (!uid || !itemNum) return;
            const twin = findMatchedTwin_po_ed_v1(matched, uid, itemNum);
            if (twin && typeof twin === "object") twin.ai_recon = { ...(twin.ai_recon || {}), ...perLine[i] };
        });
    }

    refreshDiscrepancyCount_po_ed_v1(invoiceData, items);
    return invoiceData;
};

// refresh the discrepancy count from the live (aggregate) ai_recon values
const refreshDiscrepancyCount_po_ed_v1 = (invoiceData: any, items: any[]) => {
    if (invoiceData.purchase_order_info && typeof invoiceData.purchase_order_info === "object") {
        invoiceData.purchase_order_info.count_of_items_with_discrepancies = items.reduce((acc: number, it: any) => {
            const ai = it?.reconciliation?.ai_recon || {};
            const qd = toNum_po_ed_v1(ai.difference_in_quantity);
            const pd = toNum_po_ed_v1(ai.difference_in_unit_price);
            return acc + ((it?.reconciliation && (qd !== 0 || pd !== 0)) ? 1 : 0);
        }, 0);
    }
};
